/**
 * The one data layer of the overview pages. Existing endpoints go through the shared orders API. The overview
 * endpoints (summary, hourly, daily, fleet) return the generated DTOs, whose numbers are `number | string`; the
 * mappers below turn them into the plain-number view models of ./types.ts, so pages never convert.
 */
import type {
  ApiProblem,
  FleetOverviewResponse,
  SalesSeriesResponse,
  SalesSummaryResponse,
  StoreConnectionDto,
  SyncStatusDto,
} from '@pos/api-client';
import { ordersApi } from '../orders/ordersApi';
import type {
  BusinessDay,
  DeliveryTotal,
  FleetOverviewDto,
  ProviderTotal,
  PublicationRow,
  SalesSeriesDto,
  SalesSummaryDto,
  SummaryQuery,
} from './types';

interface FetchResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

/** The part of an openapi-fetch client these calls use (the apps' clients are typed per host; paths stay plain strings). */
interface LooseClient {
  GET(path: string, init?: object): Promise<FetchResult>;
}

function unwrap<T>({ data, error, response }: FetchResult): T {
  if (!response.ok) {
    const problem = (error ?? {}) as ApiProblem;
    throw { ...problem, status: problem.status ?? response.status } satisfies ApiProblem;
  }
  return data as T;
}

const num = (value: number | string) => Number(value);
const numOrNull = (value: number | string | null) => (value === null ? null : Number(value));

export const toSummary = (d: SalesSummaryResponse): SalesSummaryDto => ({
  from: d.from,
  to: d.to,
  currencyCode: d.currencyCode,
  paidOrders: num(d.paidOrders),
  grossSales: num(d.grossSales),
  discountTotal: num(d.discountTotal),
  chargeTotal: num(d.chargeTotal),
  taxTotal: num(d.taxTotal),
  netSales: num(d.netSales),
  averageOrderValue: num(d.averageOrderValue),
  voidedOrders: num(d.voidedOrders),
  voidedValue: num(d.voidedValue),
  cancelledOrders: num(d.cancelledOrders),
  openOrders: numOrNull(d.openOrders),
  refunded: num(d.refunded),
  bySaleType: d.bySaleType.map((s) => ({ saleTypeCode: s.saleTypeCode, orders: num(s.orders), sales: num(s.sales) })),
  ordersByStatus: d.ordersByStatus.map((s) => ({ status: s.status, count: num(s.count) })),
  finishedOrdersOnly: d.finishedOrdersOnly,
});

export const toSeries = (d: SalesSeriesResponse): SalesSeriesDto => ({
  timeZoneId: d.timeZoneId,
  granularity: d.granularity,
  buckets: d.buckets.map((b) => ({ start: b.start, label: b.label, orders: num(b.orders), sales: num(b.sales) })),
});

export const toFleet = (d: FleetOverviewResponse): FleetOverviewDto => ({
  total: num(d.total),
  online: num(d.online),
  offline: num(d.offline),
  neverConnected: num(d.neverConnected),
  withBacklog: num(d.withBacklog),
  withErrors: num(d.withErrors),
  appVersions: d.appVersions,
  generatedAt: d.generatedAt,
  stores: d.stores.map((s) => ({
    storeId: s.storeId,
    code: s.code,
    name: s.name,
    status: s.status,
    online: s.online,
    appVersion: s.appVersion,
    lastHeartbeatAt: s.lastHeartbeatAt,
    lastPushAt: s.lastPushAt,
    outboxDepth: numOrNull(s.outboxDepth),
    oldestPendingAt: s.oldestPendingAt,
    failedMessages: num(s.failedMessages),
    deadMessages: num(s.deadMessages),
    conflicts24h: num(s.conflicts24h),
    lastError: s.lastError,
    printerIssues: numOrNull(s.printerIssues),
    salesToday: numOrNull(s.salesToday),
    ordersToday: numOrNull(s.ordersToday),
  })),
});

const clean = (query: Record<string, string | number | undefined>) =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ''));

/** Local day boundaries (as instants) for "today", in the browser's zone. */
export function todayRange(now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

/** Today as yyyy-mm-dd in the browser's zone. */
export function todayDate(now = new Date()) {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${String(now.getDate()).padStart(2, '0')}`;
}

export function overviewApi(client: object) {
  const api = client as LooseClient;
  const orders = ordersApi(client);
  return {
    summary: async (query: SummaryQuery = {}) =>
      toSummary(
        unwrap<SalesSummaryResponse>(
          await api.GET('/api/v1/ordering/overview/summary', { params: { query: clean({ ...query }) } }),
        ),
      ),
    hourly: async (query: { date?: string; storeId?: string; timezone?: string } = {}) =>
      toSeries(
        unwrap<SalesSeriesResponse>(
          await api.GET('/api/v1/ordering/overview/hourly', { params: { query: clean(query) } }),
        ),
      ),
    /** `days` counts back from today; `month` is `current` or `yyyy-MM` (every day up to today) and replaces `days`. */
    daily: async (query: { days?: number; month?: string; storeId?: string; timezone?: string } = {}) =>
      toSeries(
        unwrap<SalesSeriesResponse>(
          await api.GET('/api/v1/ordering/overview/daily', {
            params: { query: clean(query.month ? query : { days: 7, ...query }) },
          }),
        ),
      ),
    fleet: async (window = '24h') =>
      toFleet(unwrap<FleetOverviewResponse>(await api.GET('/api/v1/sync/fleet', { params: { query: { window } } }))),

    /** Existing endpoints. */
    paymentsByProvider: async (
      query: { from?: string; to?: string; storeId?: string } = {},
    ): Promise<ProviderTotal[]> => {
      const page = await orders.paymentTransactions({ ...query, page: 1, pageSize: 1 });
      return page.summary.map((s) => ({
        ...s,
        count: Number(s.count),
        paid: Number(s.paid),
        refunded: Number(s.refunded),
      }));
    },
    deliveriesByProvider: async (
      query: { from?: string; to?: string; storeId?: string } = {},
    ): Promise<DeliveryTotal[]> => {
      const page = await orders.shippingTransactions({ ...query, page: 1, pageSize: 1 });
      return page.summary.map((s) => ({
        ...s,
        count: Number(s.count),
        active: Number(s.active),
        delivered: Number(s.delivered),
        failed: Number(s.failed),
        fees: Number(s.fees),
      }));
    },
    publications: async (): Promise<PublicationRow[]> => {
      const page = unwrap<{ items: PublicationRow[] }>(
        await api.GET('/api/v1/sync/logs/publications', { params: { query: { page: 1, pageSize: 10 } } }),
      );
      return page.items.map((p) => ({ ...p, rowCount: Number(p.rowCount) }));
    },
    /** The open business day; the store answers 404 when none is open. */
    currentBusinessDay: async (): Promise<BusinessDay | null> => {
      const result = await api.GET('/api/v1/shifts/business-days/current');
      if (result.response.status === 404) return null;
      return unwrap<BusinessDay>(result);
    },
    /** Earlier business days whose date has passed and that a manager hasn't closed yet (ADR 0104). */
    businessDaysWaiting: async (): Promise<number> =>
      Number(unwrap<{ daysWaiting: number | string }>(await api.GET('/api/v1/shifts/business-days/hours')).daysWaiting),
    stores: async (): Promise<StoreConnectionDto[]> => unwrap(await api.GET('/api/v1/sync/stores')),
    syncStatus: async (): Promise<SyncStatusDto> => unwrap(await api.GET('/api/v1/sync/status')),
  };
}
